<template>
<div class="animated fadeIn device-detail">
  <div class="row page-header">
    <div class="col-xl-8 col-12">
      <div class="row">
        <div class="col-sm-7 col-12">
          <h2 class="page-title float-left">Plunge Pool <small>Sensor Data</small></h2>
        </div>

        <div class="col-sm-5 col-12">
          <json-excel
            type="csv"
            :data="exportData"
            :escapeCsv="false"
            class="float-right d-none d-md-block"
          ><b-button size="md" variant="success">Export</b-button>
          </json-excel>
          <b-button
            v-if="manualReadingsEnabled"
            size="md"
            variant="outline-primary"
            class="float-right mr-2 d-none d-md-inline-block"
            :disabled="localConfig.selectedDevices.length !== 1"
            :title="localConfig.selectedDevices.length === 1 ? 'Add a manual reading for the selected device' : 'Select exactly one device'"
            @click="openManualReadingModal"
          >
            <i class="fa fa-pencil-alt"></i> Add Reading
          </b-button>
          <div class="refresh-select float-md-right float-xs-left mr-2">
            <span class="switch-name">Auto Refresh?</span>
            <label class="switch switch-sm switch-text switch-info float-right mb-0 mr-1">
              <input class="switch-input" type="checkbox" v-model="localConfig.refreshEnabled">
              <span data-on="On" data-off="Off" class="switch-label"></span>
              <span class="switch-handle"></span>
            </label>
          </div>
        </div>
      </div><!-- /row -->

      <div class="row">
        <div class="col-lg-12">
          <multiselect
            id="device-select"
            v-model="localConfig.selectedDevices"
            :options="formattedDevices"
            :multiple="true"
            track-by="serial"
            label="name"
            placeholder="Select Devices"
          ></multiselect>
          <a v-if="localConfig.selectedDevices.length" href="#" class="small text-muted" @click.prevent="localConfig.selectedDevices = []">Deselect all</a>
        </div>
      </div>

      <div class="row">

        <div class="col-md-6 col-12">
          <div class="date-select mt-3">
            <date-picker
              v-model="dateRange"
              range
              confirm
              type="datetime"
              format="YYYY-MM-DD hh:mm"
              lang="en"
              width="320"
              input-class="form-control"
              range-separator="-"
              :not-after="maxDate"
              :shortcuts="dateShortcuts"
              :time-picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"
            ></date-picker>
          </div>
        </div>
        <div class="col-md-5 col-10">
          <div class="agg-select">
            <b-form-select v-model="localConfig.aggMethod" class="mb-3 mt-3 float-xs-left float-md-right" size="md" style="max-width: 20rem;">
              <option :value="null">Select aggregation options</option>
              <option :value="{period: null, fn: null}">Raw Data (no aggregation)</option>
              <optgroup label="Average">
                <option :value="{period: '15m', fn: 'mean'}">15 Minute Avg</option>
                <option :value="{period: '30m', fn: 'mean'}">30 Minute Avg</option>
                <option :value="{period: '1h', fn: 'mean'}">Hourly Avg</option>
              </optgroup>
              <optgroup label="Max">
                <option :value="{period: '15m', fn: 'max'}">15 Minute Max</option>
                <option :value="{period: '30m', fn: 'max'}">30 Minute Max</option>
                <option :value="{period: '1h', fn: 'max'}">Hourly Max</option>
              </optgroup>
              <optgroup label="Min">
                <option :value="{period: '15m', fn: 'min'}">15 Minute Min</option>
                <option :value="{period: '30m', fn: 'min'}">30 Minute Min</option>
                <option :value="{period: '1h', fn: 'min'}">Hourly Min</option>
              </optgroup>
            </b-form-select>
          </div>
        </div>
        <div class="col-md-1 col-2">
          <b-button class="option-submit mt-3 float-right" variant="success" @click="$apollo.queries.metrics.refetch()">Go</b-button>
        </div>

      </div><!-- /row -->

    </div><!-- /col-xl-8 -->

    <div class="col-xl-4 mb-2">
      <GoogleMap
        mapStyle="width:100%;height:140px;"
        :center="primaryDeviceLocation"
        :markers="deviceLocations"
      />
    </div>

  </div>

  <div class="row">
    <div class="col-xl-8 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <LineChart
            ref="metricChart"
            v-if="!loading"
            :title="'Conductivity'"
            :scale="'μS/cm'"
            :series="getSeries('ec')"
            :labels="labels"
          ></LineChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Conductivity</div>
              <strong>{{ getAverage('ec') }} μS/cm</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('ec') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-4 col-md-12">
      <b-card class="pt-4">
        <div class="chart gauge-chart">
          <GaugeChart
            ref="ecGaugeChart"
            v-if="!loading"
            :title="'Conductivity'"
            :scale="'μS/cm'"
            :value="getValue('ec')"
            :labels="labels"
            :maxValue="1000"
          ></GaugeChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Current Conductivity</div>
              <strong>{{ getValue('ec') }} μS/cm</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

  </div>
  <div class="row">

    <!--<div class="col-xl-4 col-md-12">
      <b-card class="pt-4">
        <div class="chart gauge-chart">
          <GaugeChart
            ref="depthGaugeChart"
            v-if="!loading"
            :title="'Pool Depth'"
            :scale="'feet'"
            :value="getValue('depth_f')"
            :labels="labels"
            :maxValue="8"
          ></GaugeChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Current Depth</div>
              <strong>{{ getValue('depth_f') }} feet</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>-->

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <AreaChart
            ref="tdsChart"
            v-if="!loading"
            :title="'TDS'"
            :scale="'ppm'"
            :series="getSeries('tds')"
            :labels="labels"
          ></AreaChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. TDS</div>
              <strong>{{ getAverage('tds') }} ppm</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('tds') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="warning" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

    <div class="col-xl-6 col-md-12">
      <b-card>
        <div class="chart sensor-chart">
          <AreaChart
            ref="tempChart"
            v-if="!loading"
            :title="'Temperature'"
            :scale="'Fahrenheit'"
            :series="getSeries('probe_f')"
            :labels="labels"
          ></AreaChart>
          <div v-else class="loader">
            <StretchLoader v-if="!error" />
          </div>
        </div>
        <div slot="footer">
          <ul>
            <li>
              <div class="text-muted">Avg. Temperature</div>
              <strong>{{ getAverage('probe_f') }} F</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" variant="success" :value="100"></b-progress>
            </li>
            <li>
              <div class="text-muted">Delta</div>
              <strong>{{ getDelta('probe_f') }}</strong>
              <b-progress height={} class="progress-xs mt-2" :precision="1" :value="100"></b-progress>
            </li>
          </ul>
        </div>
      </b-card>
    </div>

  </div>

  <div v-if="localConfig.selectedDevices.length === 1" class="row mt-3">
    <div class="col-12">
      <WeatherWidget :lat="primaryDeviceLocation.lat" :lng="primaryDeviceLocation.lng" :date-range="dateRange" />
    </div>
  </div>

  <ManualReadingModal
    :device="manualReadingDevice"
    :visible="showManualReadingModal"
    @close="showManualReadingModal = false"
    @created="onManualReadingChanged"
  />

</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import LineChart from '@/components/charts/LineChart.vue'
import AreaChart from '@/components/charts/AreaChart.vue'
import GaugeChart from '@/components/charts/GaugeChart.vue'
import GoogleMap from '@/components/GoogleMap.vue'
import WeatherWidget from '@/components/WeatherWidget.vue'
import ManualReadingModal from '@/components/ManualReadingModal.vue'
import DatePicker from 'vue2-datepicker'
import moment from 'moment-timezone'
import JsonExcel from 'vue-json-excel'
import gql from 'graphql-tag'

import { METRICS_QUERY, MANUAL_READINGS_QUERY } from '@/constants/graphql'
import { DATE_SHORTCUTS } from '@/constants/data'
import { MANUAL_READINGS_ENABLED } from '@/constants/features'

const GET_STATE = gql`
  query {
    config @client {
      plunge {
        refreshEnabled
        aggMethod {
          period
          fn
        }
        selectedDevices {
          name
          deviceName
          serial
        }
      }
    }
  }
`

export default {
  name: 'plunge',
  props: ['devices', 'defaultDevices'],
  data () {
    return {
      loading: 0,
      error: null,
      maxDate: new Date(Date.now() + 60000 * 30), // show up to 30 minutes in the future
      localConfig: {
        refreshEnabled: false,
        aggMethod: {
          period: '1h',
          fn: 'mean'
        },
        selectedDevices: this.defaultDevices
      },
      refreshInterval: 60000,
      refreshTimer: null,
      dateRange: [new Date(Date.now() - 3600 * 1000 * 24 * 7), new Date()], // 1 week back
      dateShortcuts: DATE_SHORTCUTS,
      showManualReadingModal: false,
      manualReadingDevice: null,
      manualReadings: []
    }
  },
  watch: {
    localConfig: {
      handler: function(data) {

        const config = {
          plunge: {
            ...data,
            __typename: 'Plunge',
            aggMethod: {
              ...data.aggMethod,
              __typename: 'PlungeAggregation'
            },
            selectedDevices: data.selectedDevices.map((d) => {
              return Object.assign({ __typename: 'Device' }, d)
            })
          }
        }

        this.$apollo
          .mutate({
            mutation: gql`
              mutation($config: Config) {
                updateConfig(config: $config) @client
              }
            `,
            variables: {
              config
            }
          })

        this.updateRefresh()
      },
      deep: true
    }
  },
  computed: {
    manualReadingsEnabled () { return MANUAL_READINGS_ENABLED },
    primaryDeviceLocation () {
      return this.deviceLocations[0].position
    },
    deviceLocations () {
      if (this.localConfig.selectedDevices.length) {
        const searchSerials = this.localConfig.selectedDevices.map(x => x.serial)
        const mapDevices = this.devices
          .filter(d => searchSerials.includes(d.serial))
          .map(d => ({ position: { lat: d.latitude, lng: d.longitude } }))

        if (mapDevices.length) {
          return mapDevices
        }
      }
      // [{position: primaryDeviceLocation}]
      return [{ position: { lat: 44.030829, lng: -70.3535825 } }] // bottling plant
    },
    formattedDevices () {
      let deviceList = []
      this.devices.forEach((device) => {
        if(device.type == 'plunge') {
          deviceList.push({
            name: device.site.name + ' (Device: ' + device.name + ')',
            deviceName: device.name,
            serial: device.serial
          })
        }
      })
      return deviceList
    },
    selectedSerials () {
      return this.localConfig.selectedDevices.map(x => x.serial)
    },
    labels () {
      let data = (this.metrics && this.metrics.data) ? this.metrics.data : null
      if (data) {
        let device = Object.keys(data)[0]
        if (data && device) return data[device].values.map(x => x[0])
      }
      return []
    },
    exportData () {
      let raw = (this.metrics && this.metrics.data.length) ? this.metrics.data : []
      let processed = []

      raw.forEach(d => {
        // set headers if not already set
        let timeIndex = this.getMetricIndex(d, 'time')
        if (!processed.length) {
          processed.push(['type', 'local_timestamp', ...d.columns])
        }
        d.values.forEach(values => {
          let time = moment(values[timeIndex]*1000).format('YYYY-MM-DD HH:mm:ss')
          processed.push([d.name, time, ...values])
        })
      })
      return processed
    }
  },
  apollo: {
    config:  {
      query: GET_STATE
    },
    metrics: {
      query: METRICS_QUERY,
      variables () {
        return {
          where: {
            device: this.selectedSerials.join('|'),
            type: 'plunge',
            metric: 'ec|tds|probe_f|depth_f',
            start: this.dateRange[0],
            end: this.dateRange[1],
            agg: this.localConfig.aggMethod.fn,
            period: this.localConfig.aggMethod.period,
            sort: 'asc',
            limit: 15000
          }
        }
      },
      skip () {
        return (!this.selectedSerials.length)
      }
    },
    manualReadings: {
      query: MANUAL_READINGS_QUERY,
      variables () {
        return {
          where: {
            deviceSerial_in: this.selectedSerials,
            timestamp_gte: this.dateRange[0],
            timestamp_lte: this.dateRange[1]
          },
          orderBy: { timestamp: 'asc' },
          limit: 5000
        }
      },
      skip () {
        return (!MANUAL_READINGS_ENABLED || !this.selectedSerials.length)
      },
      error () { /* silently ignore */ }
    }
  },
  methods: {
    openManualReadingModal () {
      if (this.localConfig.selectedDevices.length !== 1) return
      const selected = this.localConfig.selectedDevices[0]
      const fullDevice = this.devices.find(d => d.serial === selected.serial)
      this.manualReadingDevice = fullDevice || { serial: selected.serial, name: selected.deviceName, type: 'plunge' }
      this.showManualReadingModal = true
    },
    onManualReadingChanged () {
      this.showManualReadingModal = false
      if (this.$apollo.queries.manualReadings) {
        this.$apollo.queries.manualReadings.refetch()
      }
    },
    manualSeriesForMetric (metric) {
      var self = this
      if (!self.manualReadings || !self.manualReadings.length) return []
      var bySerial = {}
      self.manualReadings.forEach(function (r) {
        if (r.metricName !== metric) return
        var serial = (r.device && r.device.serial) || null
        if (!serial) return
        if (!bySerial[serial]) bySerial[serial] = { verified: [], unverified: [] }
        var bucket = r.isFieldVerified ? 'verified' : 'unverified'
        bySerial[serial][bucket].push([new Date(r.timestamp).getTime(), r.value])
      })
      var series = []
      Object.keys(bySerial).forEach(function (serial) {
        var deviceObj = self.getDeviceBySerial(serial)
        var label = (deviceObj && deviceObj.name) || serial
        if (bySerial[serial].verified.length) {
          series.push({
            name: label + ' (field verified)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#28a745', lineColor: '#1d6e34', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].verified
          })
        }
        if (bySerial[serial].unverified.length) {
          series.push({
            name: label + ' (manual)',
            type: 'scatter',
            marker: { symbol: 'diamond', radius: 6, fillColor: '#ffc107', lineColor: '#a8800a', lineWidth: 1 },
            lineWidth: 0,
            data: bySerial[serial].unverified
          })
        }
      })
      return series
    },
    updateRefresh () {
      if (this.localConfig.refreshEnabled && !this.refreshTimer) {
        this.refreshTimer = setInterval(() => {
          // push out date for date picker
          this.maxDate = new Date(Date.now() + 60000 * 30)
          this.dateRange = [
            moment(this.dateRange[0]).add(this.refreshInterval, 'ms').toDate(),
            moment(this.dateRange[1]).add(this.refreshInterval, 'ms').toDate()
          ]
          this.$apollo.queries.metrics.refetch()
        }, this.refreshInterval)
      } else {
        clearInterval(this.refreshTimer)
      }
    },
    getSeries (metric) {
      // console.time('getSeries')
      metric = metric || 'ec'
      const timeIndex = 2

      let series = []
      let metricName = (this.localConfig.aggMethod && this.localConfig.aggMethod.fn)
        ? `${this.localConfig.aggMethod.fn}_${metric}`
        : metric

      if (!this.metrics || !this.metrics.data) {
        return series
      }

      Object.keys(this.metrics.data).forEach((device) => {
        let deviceData = this.metrics.data[device]
        let deviceObj = this.getDeviceBySerial(deviceData.tags.device_id)
        let metricIndex = this.getMetricIndex(deviceData, metricName)

        series.push({
          name: (deviceObj) ? deviceObj.name : deviceData.tags.device_id,
          data: deviceData.values.map(x => [x[timeIndex] * 1000, x[metricIndex]])
        })
      })
      // Overlay manual readings as additional scatter series
      var manualSeries = this.manualSeriesForMetric(metric)
      if (manualSeries.length) {
        manualSeries.forEach(function (s) { series.push(s) })
      }
      // console.timeEnd('getSeries')
      return series
    },
    getValue (metric, latest) {
      metric = metric || 'ec'
      latest = (latest !== undefined) ? latest : true

      let data = this.getSeries(metric)
      if (!data || data.length === 0) {
        return null
      }

      let sliceIndex = (latest) ? -2 : 1

      let values = data.map((d) => {
        return d.data.slice(sliceIndex)[0][1]
      })

      if (values.length > 0) {
        let sum = values.reduce((a, b) => a + b, 0)
        return parseFloat((sum / values.length).toFixed(1))
      }

      return 0
    },
    getAverage (metric) {
      metric = metric || 'ec'
      let series = this.getSeries(metric)

      if (!series || !series[0]) {
        return null
      }

      let values = series[0].data.map(d => d[1])
      let sum = values.reduce((a, b) => a + b, 0)
      return parseFloat((sum / values.length).toFixed(1))
    },
    getDelta (metric) {
      metric = metric || 'ec'
      let start = this.getValue(metric, false)
      let end = this.getValue(metric, true)

      if (start === 0) {
        return '0%'
      }

      let delta = 100 * Math.abs( ( end - start ) / ( (end + start) / 2 ) )

      if (delta === 0) {
        return '0%'
      } else {
        return (delta > 0) ? `+${delta.toFixed(1)}%` : `${delta.toFixed(1)}%`
      }
    },
    getDeviceBySerial (serial) {
      return this.devices.filter((device) => {
        return device.serial === serial
      })[0]
    },
    getMetricIndex (deviceData, metricName) {
      for (let i in deviceData.columns) {
        if (deviceData.columns[i] === metricName) {
          return i
        }
      }
      return -1
    }
  },
  beforeMount () {
    if (this.config && this.config.plunge) {
      this.localConfig = JSON.parse(JSON.stringify(this.config.plunge))
    }
    if (!this.localConfig.selectedDevices || !this.localConfig.selectedDevices.length) {
      this.localConfig.selectedDevices = this.defaultDevices
    }
  },
  beforeDestroy () {
    clearInterval(this.refreshTimer)
  },
  components: {
    Multiselect,
    LineChart,
    AreaChart,
    GaugeChart,
    DatePicker,
    JsonExcel,
    GoogleMap,
    WeatherWidget,
    ManualReadingModal
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
</style>
